import React from 'react';
import { Trash2, Plus } from 'lucide-react';
import type { ItemSource, OrderLineInput } from '../../types';
import { ITEM_SOURCE_LABEL } from '../../pages/admin/orderUtils';
import MoneyInput from './MoneyInput';

interface Props {
  lines: OrderLineInput[];
  onChange: (lines: OrderLineInput[]) => void;
}

const blankLine = (): OrderLineInput => ({
  product_name: '',
  description: '',
  qty: 1,
  unit_price_cents: 0,
  item_source: 'na',
});

const centsToDollars = (c: number): string => (c / 100).toFixed(2);

const OrderLinesEditor: React.FC<Props> = ({ lines, onChange }) => {
  const update = (idx: number, patch: Partial<OrderLineInput>) => {
    onChange(lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };
  const remove = (idx: number) => onChange(lines.filter((_, i) => i !== idx));
  const add = () => onChange([...lines, blankLine()]);

  return (
    <div className="space-y-3">
      <div className="text-sm font-medium">Line items</div>
      {lines.length === 0 && (
        <div className="text-sm text-text-light italic">
          No lines yet. Add at least one before sending an invoice.
        </div>
      )}
      {lines.map((line, idx) => {
        const lineTotal = (line.qty || 0) * (line.unit_price_cents || 0);
        return (
          <div key={idx} className="border border-background-dark rounded p-3 space-y-2">
            <div className="grid grid-cols-12 gap-2 items-start">
              <input
                value={line.product_name}
                onChange={e => update(idx, { product_name: e.target.value })}
                placeholder="Item name"
                className="col-span-5 border border-background-dark rounded px-2 py-1 text-sm"
              />
              <input
                type="number"
                min={1}
                value={line.qty}
                onChange={e => update(idx, { qty: Math.max(1, Number(e.target.value) || 1) })}
                className="col-span-2 border border-background-dark rounded px-2 py-1 text-sm text-right"
              />
              <div className="col-span-3 relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-text-light">$</span>
                <MoneyInput
                  valueCents={line.unit_price_cents}
                  onChange={cents => update(idx, { unit_price_cents: cents })}
                  className="w-full border border-background-dark rounded pl-5 pr-2 py-1 text-sm text-right"
                />
              </div>
              <div className="col-span-2 text-right text-sm py-1">
                ${centsToDollars(lineTotal)}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2 items-start">
              <input
                value={line.description ?? ''}
                onChange={e => update(idx, { description: e.target.value })}
                placeholder="Description / personalization detail"
                className="col-span-8 border border-background-dark rounded px-2 py-1 text-sm"
              />
              <select
                value={line.item_source ?? 'na'}
                onChange={e => update(idx, { item_source: e.target.value as ItemSource })}
                className="col-span-3 border border-background-dark rounded px-2 py-1 text-sm bg-white"
              >
                {(['na', 'customer_provided', 'shop_provided', 'unsure'] as ItemSource[]).map(s => (
                  <option key={s} value={s}>
                    {ITEM_SOURCE_LABEL[s]}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => remove(idx)}
                className="col-span-1 text-red-600 hover:bg-red-50 rounded p-1 justify-self-end"
                title="Remove line"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        );
      })}
      <button
        type="button"
        onClick={add}
        className="border border-background-dark rounded px-3 py-1.5 text-sm inline-flex items-center gap-1 hover:border-primary"
      >
        <Plus size={14} /> Add line
      </button>
    </div>
  );
};

export default OrderLinesEditor;
